import React, { useState, useRef, useEffect } from 'react';

import Axios from 'axios';

import styled from 'styled-components';

import { useDeleteMediaList, onContactMediaUpdate } from '@hooks/API/Contact/UseContactMedia';
import { useAdjustTagUsageCount } from '@hooks/CachedLists/UseTagCatalog';

import Comments from '@components/Comments/Comments';
import FlexContainer from '@components/Gui/FlexContainer';
import { Loading } from '@components/Gui/Loading';
import { NwButton } from '@components/Gui/NwButton';
import { NwCard } from '@components/Gui/NwCard/NwCard';
import NwDrawer from '@components/Gui/NwDrawer';
import NwIcon from '@components/Gui/NwIcon';
import ModelCoverGenerator from '@components/ImageCroppers/ModelCoverGenerator';
//import EditImage from '@components/MediaDrawer/EditImage';
import MediaBooks from '@components/MediaDrawer/MediaBooks';
import MediaPreview from '@components/MediaDrawer/MediaPreview';
import MediaRatings from '@components/MediaDrawer/MediaRatings';
import ObjectLogHistories from '@components/ObjectLogHistories/ObjectLogHistories';
import ObjectTagsCard from '@components/ObjectTagsCard/ObjectTagsCard';

import EditImageNew from './EditImageNew';
import MediaDetails from './MediaDetails';
import VideoCoverSelector from './VideoCoverSelector';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const CardContainer = styled.div`
    margin-top: 1rem;
`;

const Actions = styled(FlexContainer)`
    svg {
        margin-left: 8px;
    }
`;

const Link = styled.div`
    cursor: pointer;
`;

const EncodingVideoMessage = styled.div`
    background-color: black;
    color: white;
    padding: 0;
    height: 15rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 3rem;
    h1 {
        font-size: 2rem;
        margin: 0 0 2rem 0;
        font-weight: 500;
        line-height: 1.5em;
        color: white;
    }
`;

const RateableMedia = ['Photo', 'Video'];

const MediaDrawer = ({
    mediaID,
    contactID,
    isShowComment,
    isEditDetail,
    afterMediaUpdate,
    onSelectMediaBefore,
    onSelectMediaAfter,
    onCloseDrawer,
    afterNewMediaCreation,
    viewOnly = false,
}) => {
    const [workingOn, setWorkingOn] = useState(null);
    const [workingMessage, setWorkingMessage] = useState(null);
    const [document, setDocument] = useState(null);
    const [moveComment, setMoveComment] = useState(false);
    const [moveDetail, setMoveDetail] = useState(false);
    const commentsRef = useRef();
    const detailRef = useRef();
    const ratingsRef = useRef();
    const tagsRef = useRef();
    const { mutateAsync: onDeleteMediaList } = useDeleteMediaList();
    const adjustTagUsageCount = useAdjustTagUsageCount();
    const [touchForm, setTouchForm] = useState(false);
    const [showCropDocument, setShowCropDocument] = useState(false);
    const [editDocumentImage, setEditDocumentImage] = useState(false);

    const getMedia = async () => {
        const documentData = await Axios.get(`/documents/${mediaID}`);
        setDocument(documentData);
    };

    useEffect(() => {
        if (mediaID) {
            getMedia();
        }
    }, [mediaID]);

    const handleImageCreated = (newMediaID) => {
        setEditDocumentImage(false);
        afterNewMediaCreation();
    };

    useEffect(() => {
        if (isShowComment) {
            setMoveComment(true);
        }
        if (isEditDetail) {
            setMoveDetail(true);
        }
    }, []);

    useEffect(() => {
        if (moveComment && commentsRef.current && document) {
            handleShowComments();
            setMoveComment(false);
        }
    }, [moveComment, commentsRef.current, document]);

    useEffect(() => {
        if (moveDetail && detailRef.current && document) {
            handleShowInfo();
            setMoveDetail(false);
        }
    }, [moveDetail, detailRef.current, document]);

    const confirmDelete = () => {
        deleteMedia();
    };

    const afterChanges = async () => {
        if (contactID) {
            onContactMediaUpdate(contactID);
        }
        if (afterMediaUpdate) {
            afterMediaUpdate();
        }
    };

    const deleteMedia = async () => {
        setWorkingOn(mediaID);
        setWorkingMessage('Deleting Media...');
        await onDeleteMediaList({ documentIDs: [mediaID], contactID: contactID });
        await afterChanges();
        onCloseDrawer();
    };

    const handleSaveChanges = () => {
        afterChanges();
    };

    const handleDocumentUpdate = async (updateType, item) => {
        let comments = [...document.Comments];
        if (updateType === 'delete') {
            if (item) {
                comments = document.Comments.filter((comment) => comment.ID !== item);
            }
        } else if (updateType === 'save') {
            const commentIndex = document.Comments.findIndex((comment) => comment.ID === item.ID);
            if (commentIndex > -1) {
                comments[commentIndex] = item;
            } else {
                comments.push(item);
            }
        }
        getMedia();
        await afterChanges();
    };

    const handleAddTag = async (tag) => {
        try {
            await Axios.post(`/documents/addtagmulti?tagID=${parseInt(tag.ID)}&contactID=${contactID}`, [document.ID]);
            adjustTagUsageCount(tag.ID, 1);
            getMedia();
            await afterChanges();
        } catch (error) {
            console.error('errore', error);
        }
    };

    const handleRemoveTag = async (tag) => {
        try {
            await Axios.post(`/documents/removetagmulti?tagID=${tag.ID}&contactID=${contactID}`, [document.ID]);
            adjustTagUsageCount(tag.ID, -1);
            getMedia();
            await afterChanges();
        } catch (error) {
            console.error('errore', error);
        }
    };

    const handleMediaUpdate = async () => {
        getMedia();
        await afterChanges();
    };

    const handleShowComments = () => {
        commentsRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    };

    const handleShowInfo = () => {
        detailRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
    };

    const handleShowCropDocumentImage = () => {
        setShowCropDocument(true);
    };

    const handleCloseCropDocumentImage = () => {
        setShowCropDocument(false);
    };

    const handleEditDocumentImage = () => {
        setEditDocumentImage(true);
    };

    const handleCloseEditDocumentImage = () => {
        setEditDocumentImage(false);
    };

    const handleViewWebVideo = () => {
        if (document) {
            window.open(document.Standard.Url, '_blank');
        }
    };

    const isEncodingVideo = (document) => {
        return document.Type === 'Video' && document.Provider === 'CloudFiles' && document.Status !== 0;
    };

    return (
        <NwDrawer
            width={window.innerWidth > 876 ? '70vw' : '100%'}
            title={
                <FlexContainer justifyContent="space-between">
                    <div>Media Details</div>
                    <Actions>
                        {document && document.Type === 'Photo' && (
                            <NwButton
                                ghost
                                size="small"
                                label="use as profile picture"
                                icon={byPrefixAndName.fasl['user-circle']}
                                onClick={handleShowCropDocumentImage}
                            />
                        )}
                        {document && document.Type === 'Photo' && (
                            <NwButton
                                icon={byPrefixAndName.fasl['pen']}
                                ghost
                                size="small"
                                label="edit"
                                onClick={handleEditDocumentImage}
                            />
                        )}
                    </Actions>
                </FlexContainer>
            }
            onClose={() => onCloseDrawer(document)}
            touchForm={touchForm}
            onTouchForm={setTouchForm}
            whiteBg={false}
        >
            {editDocumentImage ? (
                <EditImageNew
                    contactID={contactID}
                    image={document}
                    onImageCreated={handleImageCreated}
                    onCancel={handleCloseEditDocumentImage}
                />
            ) : (
                <div className="drawer-content">
                    {workingOn || !document ? (
                        <div>
                            <Loading />
                            <p>{workingMessage}</p>
                        </div>
                    ) : (
                        document && (
                            <>
                                <div id="drawerContentContainer">
                                    <div>
                                        {!isEncodingVideo(document) ? (
                                            <>
                                                <MediaPreview
                                                    document={document}
                                                    onSelectMediaBefore={onSelectMediaBefore}
                                                    onSelectMediaAfter={onSelectMediaAfter}
                                                />
                                                {document.Provider === 'Youtube' && (
                                                    <NwCard
                                                        title={document.Provider}
                                                        icon={byPrefixAndName.fab['youtube']}
                                                        horizontal
                                                    >
                                                        <Link onClick={handleViewWebVideo}>
                                                            {document.Standard.Url}
                                                        </Link>
                                                    </NwCard>
                                                )}
                                                {document.Provider === 'Vimeo' && (
                                                    <NwCard
                                                        title={document.Provider}
                                                        icon={byPrefixAndName.fab['vimeo']}
                                                        horizontal
                                                    >
                                                        <Link onClick={handleViewWebVideo}>
                                                            {document.Standard.Url}
                                                        </Link>
                                                    </NwCard>
                                                )}
                                            </>
                                        ) : (
                                            <EncodingVideoMessage>
                                                <h1>This video is still encoding, or failed to encode</h1>
                                                <NwIcon icon={byPrefixAndName.fasl['camera-movie']} beat />
                                            </EncodingVideoMessage>
                                        )}
                                        {document && RateableMedia.includes(document.Type) && (
                                            <CardContainer fullWidth ref={ratingsRef}>
                                                <MediaRatings
                                                    objectType={'Document'}
                                                    objectId={document.ID}
                                                    onMediaUpdate={handleMediaUpdate}
                                                />
                                            </CardContainer>
                                        )}
                                        <CardContainer fullWidth ref={tagsRef}>
                                            <ObjectTagsCard
                                                objectType={'Document'}
                                                objectTags={document.Tags}
                                                onAddTag={handleAddTag}
                                                onRemoveTag={handleRemoveTag}
                                            />
                                        </CardContainer>
                                        <div ref={detailRef}>
                                            <MediaDetails
                                                onSaveChanges={handleSaveChanges}
                                                document={document}
                                                viewOnly={viewOnly}
                                                contactID={contactID}
                                                isEditDetail={isEditDetail}
                                                onTouchForm={setTouchForm}
                                                confirmDelete={confirmDelete}
                                            />
                                        </div>
                                        {document && document.Type === 'Video' && (
                                            <CardContainer>
                                                <VideoCoverSelector
                                                    documentID={document.ID}
                                                    contactID={contactID}
                                                    onCoverUpdate={getMedia}
                                                />
                                            </CardContainer>
                                        )}
                                        {document && (document.Type === 'Video' || document.Type === 'Photo') && (
                                            <MediaBooks documentID={document.ID} contactID={contactID} />
                                        )}
                                        {document && (
                                            <CardContainer ref={commentsRef}>
                                                <Comments
                                                    api="DocumentID"
                                                    comments={document.Comments}
                                                    ID={document.ID}
                                                    disableVisibility
                                                    drawerMode
                                                    onTouchForm={setTouchForm}
                                                    onUpdate={handleDocumentUpdate}
                                                    isPaginated={false}
                                                />
                                            </CardContainer>
                                        )}
                                    </div>
                                </div>
                            </>
                        )
                    )}
                    {showCropDocument && (
                        <ModelCoverGenerator
                            contactID={contactID}
                            imageUrl={document.HD && document.HD.Url ? document.HD.Url : document.Standard.Url}
                            onClose={handleCloseCropDocumentImage}
                        />
                    )}
                    {/* {editDocumentImage &&
                        <NwDrawer
                            width={"80vw"}
                            title="Edit Image"
                            padding="0"
                            onClose={handleCloseEditDocumentImage}>
                            <EditImage
                                contactID={contactID}
                                image={document}
                                onImageCreated={handleImageCreated}/>
                        </NwDrawer>
                    } */}
                </div>
            )}
        </NwDrawer>
    );
};

export default MediaDrawer;
